import { IInterestManagementData } from '@/app/interest-management/entities/interest-management-data.interface';
import { IInterestManagementState } from '@/app/interest-management/entities/interest-management-state.interface';
import { IInterestTypeSelection } from '@/app/interest-management/entities/interest-type-selection.interface';
import { INoticeState } from '@/app/interest-management/entities/notice-state.interface';
import { InterestManagementFactory } from '@/app/interest-management/providers/interest-management.factory';
import { IInterestState } from '@/app/interest-management/providers/interest-state.interface';
import { InterestType } from '@/app/subscription-ui/entities/interest.type';
import { IFormGroupState } from '@/components/atoms/form/form-group/form-group-state.interface';
import { Ref, ref } from '@vue/reactivity';
import { ObjectUtils, SubscriptionEntity, TranslationKey, TranslationService, TranslationsJson } from '../../../../modules';
import { InterestEntity } from '../../../../modules/resource/interest/interest-entity.interface';

export class InterestManagementState {

    private _state: Ref<IInterestManagementState>;

    constructor(data: IInterestManagementData) {
        this._state = ref<IInterestManagementState>(InterestManagementFactory.initialState(data));
    }

    get clone(): IInterestManagementState {
        return ObjectUtils.clone<IInterestManagementState>(this.state);
    }

    get state(): IInterestManagementState {
        return this._state.value;
    }

    set state(value: IInterestManagementState) {
        this._state.value = value;
    }

    async decorateInterestsWithTranslation(service: TranslationService): Promise<void> {
        let collection: TranslationsJson | null = await service.getTranslations();
        if (collection === null) {
            return;
        }

        let clone: IInterestManagementState | null = this.clone;
        this.state = {
            ...clone,
            eventimInterests: clone.eventimInterests.map(interest => {
                if (interest.resourceName === 'general') {
                    interest.title = collection !== null ? collection[TranslationKey.Interest_subscription_general_interest_name] : '';
                    interest.description = collection !== null ? collection[TranslationKey.Interest_subscription_general_interest_description] : '';
                }

                if (interest.resourceName === 'offers') {
                    interest.title = collection !== null ? collection[TranslationKey.Interest_subscription_special_interest_name] : '';
                    interest.description = collection !== null ? collection[TranslationKey.Interest_subscription_special_interest_description] : '';
                }

                return interest;
            }),
        }

        collection = null;
        clone = null;
    }

    getSubscriptionPayload(): SubscriptionEntity {
        let consentState: IInterestState | undefined | null = this.state.eventimInterests.find(i => i.type === InterestType.Consent);

        if (consentState === undefined) {
            throw new Error('Consent interest missing in interest collection!');
        }

        const update: SubscriptionEntity = {
            userEmail: this.state.email.value,
            interests: this.state.interests,
            removedInterests: this.state.removedInterests,
        };

        if (consentState.state !== consentState.initialState) {
            update.marketingConsent = consentState.state;
        }

        if (this.state.noContactInterest.state !== this.state.noContactInterest.initialState) {
            update.doNotContactMe = this.state.noContactInterest.state;
        }

        consentState = null;
        return update;
    }

    setProgress(state: boolean, type: 'interest'|'artistAlarm'): void {
        if (type === 'artistAlarm') {
            this.state = { ...this.clone, artistInterestRequestInProgress: state };
            return;
        }

        this.state = { ...this.clone, topicInterestRequestInProgress: state };
    }

    updateAutomaticSubscriptionWith(value: boolean): void {
        this.state = { ...this.clone, automaticSubscription: value };
    }

    updateArtistQueryWith(change: IFormGroupState): void {
        let clone: IInterestManagementState | null = this.clone;

        this.state = { ...clone, artistInterestQuery: { ...clone.artistInterestQuery, name: change } };
        clone = null;
    }

    updateArtistResultsWith(collection: IInterestState[]): void {
        this.state = { ...this.clone, artistInterestResults: collection, artistInterestRequestInProgress: false };
    }

    updateNoContactWith(interestUpdate: IInterestState): void {
        this.state = { ...this.clone, noContactInterest: interestUpdate };
    }

    updateEmail(data: IFormGroupState): void {
        this.state = { ...this.clone, email: data };
    }

    updateInterestQueryWith(change: IFormGroupState): void {
        let clone: IInterestManagementState | null = this.clone;

        this.state = { ...clone, topicInterestQuery: { ...clone.topicInterestQuery, name: change } };
        clone = null;
    }

    updateInterestResultsWith(collection: IInterestState[]): void {
        this.state = { ...this.clone, topicInterestResults: collection, topicInterestRequestInProgress: false };
    }

    updateInterestTypeWith(change: IInterestTypeSelection): void {
        let clone: IInterestManagementState | null = this.clone;

        this.state = { ...clone, topicInterestQuery: { ...clone.topicInterestQuery, type: change } };
        clone = null;
    }

    updateAutomaticSubscriptionNotice(state: INoticeState): void {
        let update: IInterestManagementState | null = this.clone;

        update.automaticSubscriptionNotice = state;
        this.state = update;
        update = null;
    }

    updateNotice(state: INoticeState): void {
        let update: IInterestManagementState | null = this.clone;

        update.notice = state;
        this.state = update;
        update = null;
    }

    updateStateFor(interest: IInterestState): void {
        let update: IInterestManagementState | null = this.clone;
        let entity: InterestEntity | null = {
            type: interest.type,
            resourceId: interest.resourceId,
            resourceName: interest.resourceName
        };

        if (interest.state && interest.resourceId !== -1) {
            update.interests = update.interests.concat(entity);
            update.removedInterests = update.removedInterests.filter(i => i.type !== interest.type && i.resourceId !== interest.resourceId);
        }

        if (!interest.state && interest.resourceId !== -1) {
            update.removedInterests = update.removedInterests.concat(entity);
            update.interests = update.interests.filter(i => !(i.type === interest.type && i.resourceId === interest.resourceId));
        }

        if (interest.type === InterestType.General) {
            update.eventimInterests = update.eventimInterests.map(i => i.resourceId === interest.resourceId ? interest : i);
        }

        if (interest.type === InterestType.Consent) {
            update.eventimInterests = update.eventimInterests.map(i => i.resourceId === interest.resourceId ? interest : i);
        }

        if (interest.type === InterestType.Artist) {
            update.artistInterests = this.getUpdatedCollectionFor(update.artistInterests, interest);
            update.artistInterestResults = update.artistInterestResults.map(i => i.resourceId === interest.resourceId ? interest : i);
        }

        if (interest.type === InterestType.Category  || interest.type === InterestType.MainCategory || interest.type === InterestType.Venue) {
            update.topicInterests = this.getUpdatedCollectionFor(update.topicInterests, interest);
            update.topicInterestResults = update.topicInterestResults.map(i => i.resourceId === interest.resourceId ? interest : i);
        }

        this.state = update;
        entity = null;
        update = null;
    }

    private getUpdatedCollectionFor(state: IInterestState[], interest: IInterestState): IInterestState[] {

        return state.some(i => i.resourceId === interest.resourceId)
            ? state.filter(i => i.resourceId !== interest.resourceId)
            : state.concat(interest);
    }

}
